import React, { memo } from 'react';
import NodeRenderer from '../NodeRenderer/index';
import QuickViewPreview from '../QuickViewPreview';
import CampaignZones from '../CampaignZones';
import ChainHeaders from '../ChainHeaders';
import {
  CanvasGrid,
  CanvasEdgesLayer,
  ConnectionPreviewLayer,
  SelectionBox,
} from './components';
import { useFlowchartCanvas } from './hooks';
import buildNodeRendererProps from './utils/buildNodeRendererProps';
import areFlowchartCanvasPropsEqual from './utils/areFlowchartCanvasPropsEqual';

/**
 * FlowchartCanvas Component
 *
 * Renders the main canvas area with edges, nodes, selection box, and empty state.
 * Extracted from FlowchartView to reduce file size.
 *
 * PERFORMANCE: Uses ref-based transform updates to prevent React from
 * overwriting direct DOM manipulation during pan operations.
 */
const FlowchartCanvas = (props) => {
  const {
    canvasRef,
    gridBounds,
    quickView,
    onNavigateToParentMission,
    isFocusAnimating,
  } = useFlowchartCanvas(props);

  const {
    isMultiCampaignMode,
    tasks,
    multiCampaignCampaigns,
    positionsToUse,
    nodeDimensions,
    isAnimatingLayout,
    containerRef,
    pan,
    zoom,
    onSelectSprint,
    onRemoveCampaign,
    removingCampaignId,
    chainInfo,
    chains,
    onChainMoveLeft,
    onChainMoveRight,
    isFocusMode,
    draggingNode,
    isConnecting,
    edgesToRender,
    selectedEdge,
    reconnectingEdge,
    tasksToRender,
    handleEdgeClick,
    handleEdgeArrowDragStart,
    displayTasks,
    displayPositions,
    hiddenConnections,
    connectingFrom,
    connectingTarget,
    connectingBranch,
    connectingStartPos,
    mousePos,
    selectionBox,
    statuses,
    allCategories,
    allTags,
    cardDisplaySettings,
  } = props;

  return (
    <div
      ref={canvasRef}
      className="flowchart__canvas"
      style={{ transformOrigin: '0 0' }}
    >
      <CanvasGrid gridBounds={gridBounds} />

      {isMultiCampaignMode && (
        <CampaignZones
          tasks={tasks}
          campaigns={multiCampaignCampaigns}
          positions={positionsToUse}
          nodeDimensions={nodeDimensions}
          isAnimating={isAnimatingLayout || isFocusAnimating}
          containerRef={containerRef}
          pan={pan}
          zoom={zoom}
          onCampaignClick={onSelectSprint}
          onRemoveCampaign={onRemoveCampaign}
          removingCampaignId={removingCampaignId}
        />
      )}

      <ChainHeaders
        tasks={tasks}
        positions={positionsToUse}
        nodeDimensions={nodeDimensions}
        chainInfo={chainInfo}
        chains={chains}
        onMoveLeft={onChainMoveLeft}
        onMoveRight={onChainMoveRight}
        isFocusMode={isFocusMode}
        isDragging={!!draggingNode}
        isMultiCampaignMode={isMultiCampaignMode}
      />

      <CanvasEdgesLayer
        isFocusMode={isFocusMode}
        edgesToRender={edgesToRender}
        selectedEdge={selectedEdge}
        reconnectingEdge={reconnectingEdge}
        tasksToRender={tasksToRender}
        handleEdgeClick={handleEdgeClick}
        handleEdgeArrowDragStart={handleEdgeArrowDragStart}
        displayTasks={displayTasks}
        displayPositions={displayPositions}
        nodeDimensions={nodeDimensions}
        hiddenConnections={hiddenConnections}
      />

      <NodeRenderer
        {...buildNodeRendererProps(props, { quickView, onNavigateToParentMission })}
      />

      {isConnecting && (
        <ConnectionPreviewLayer
          connectingFrom={connectingFrom}
          connectingTarget={connectingTarget}
          connectingBranch={connectingBranch}
          connectingStartPos={connectingStartPos}
          displayPositions={displayPositions}
          nodeDimensions={nodeDimensions}
          mousePos={mousePos}
          tasks={tasks}
        />
      )}

      <SelectionBox selectionBox={selectionBox} />

      {quickView.isQuickViewEnabled && quickView.quickViewTask && quickView.quickViewMousePos && (
        <QuickViewPreview
          task={quickView.quickViewTask}
          statuses={statuses}
          allCategories={allCategories}
          allTags={allTags}
          nestedSprint={quickView.quickViewNestedSprint}
          cardDisplaySettings={cardDisplaySettings}
          mousePosition={quickView.quickViewMousePos}
        />
      )}
    </div>
  );
};

FlowchartCanvas.displayName = 'FlowchartCanvas';

export default memo(FlowchartCanvas, areFlowchartCanvasPropsEqual);
